import { useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { useAuth } from "./useAuth"
import { errorHandler } from "@/utils/errorHandler"
import { logger } from "@/utils/logger"

export const useCreateBroadcastMessage = () => {
  const queryClient = useQueryClient()
  const { profile } = useAuth()

  return useMutation({
    mutationFn: async (data: {
      title: string
      content: string
      targetRole?: string
    }) => {
      if (!profile?.id) {
        const error = new Error("Usuário não autenticado")
        logger.error("Tentativa de comunicado sem autenticação", "useCreateBroadcastMessage")
        errorHandler.handleError({
          code: 'PERMISSION_DENIED',
          message: error.message
        })
        throw error
      }

      try {
        logger.info("Criando comunicado", "useCreateBroadcastMessage", {
          title: data.title,
          userId: profile.id,
          targetRole: data.targetRole
        })

        // Usar a nova função RPC para criar broadcast
        const { data: conversationId, error: rpcError } = await supabase
          .rpc('create_broadcast_conversation', {
            broadcast_title: data.title,
            broadcast_message: data.content,
            target_role: data.targetRole || null
          })

        if (rpcError) {
          logger.error("Erro ao criar broadcast via RPC", "useCreateBroadcastMessage", rpcError)
          errorHandler.handleError({
            code: 'BROADCAST_CREATE_ERROR',
            message: rpcError.message,
            details: rpcError
          })
          throw rpcError
        }

        logger.info("Broadcast criado com sucesso via RPC", "useCreateBroadcastMessage", { 
          conversationId
        })

        return { conversationId }
      } catch (error) {
        logger.error("Erro na criação do broadcast", "useCreateBroadcastMessage", error)
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] })
      logger.info("Broadcast enviado - queries invalidadas", "useCreateBroadcastMessage")
      toast.success("Comunicado enviado com sucesso!")
    },
    onError: (error: any) => {
      logger.error("Erro no callback de broadcast", "useCreateBroadcastMessage", error)
      toast.error("Erro ao enviar comunicado. Tente novamente.")
    }
  })
}